import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useProjectsHoursAPI } from 'src/API/central.projectsAPI'
import { useUsersAPI } from 'src/API/central.usersAPI'
import { useUserTaskAPI } from 'src/API/central.usersTaskAPI'
import { stringToColor } from 'src/lib/utils'
import { Card, CardContent, CardHeader, CardTitle } from 'src/shadcn/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/shadcn/ui/tabs'
import { ProjectComponentTaskVarient } from '../Tasks/component.projectCard'
import { Badge } from 'src/shadcn/ui/badge'
// import { Avatar, AvatarFallback, AvatarImage } from 'src/shadcn/ui/avatar'
import { Progress } from 'src/shadcn/ui/progress'
import { Avatar, AvatarFallback, AvatarImage } from 'src/shadcn/ui/avatar'
import { nestWorkItems, WorkItemList } from '../my_works/component.projectCard'
import { useCentralAPI, WorkItemTypeCentral } from 'src/API/centralAPI'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'src/shadcn/ui/react-dialog'
import { IconCalendar, IconReport } from '@tabler/icons-react'
import { IconDashboard, IconListCheck, IconFolder } from '@tabler/icons-react'
// import { Calendar } from 'src/shadcn/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from 'src/shadcn/ui/popover'
import { Button } from 'src/shadcn/ui/button'
import { format } from 'date-fns'
import { CalendarIcon, Clock } from 'lucide-react'
import { Calendar } from 'src/shadcn/ui/calender'
import { DayWiseEntry, useWorkItemsAPIAll } from 'src/API/central.workItem'

interface WorkItem {
  fields: {
    'System.WorkItemType': string;
    [key: string]: any;
  };
  organizationId: string;
  [key: string]: any;
}

interface ProjectCardProps {
  project: {
    id: string;
    name: string;
    projectStatus: string;
    completedWorkItem: number;
    totalWorkItem: number;
    lastUpdateTime: string;
    workItems: WorkItemTypeCentral[];
    hours: number
  };
  onClick: (project: any) => void;
}

function ProjectCard({ project, onClick }: ProjectCardProps) {
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'inprogress': return 'bg-blue-500'
      case 'completed': return 'bg-green-500'
      case 'onhold': return 'bg-yellow-500'
      default: return 'bg-gray-500'
    }
  }



  return (
    <Card
      key={project.id}
      className="w-full cursor-pointer hover:shadow-lg transition-shadow"
      onClick={() => onClick(project)}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-2xl font-bold">{project.name}</CardTitle>
        <Badge variant="outline" className={getStatusColor(project.projectStatus)}>
          {project.projectStatus}
        </Badge>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            {/* Progress Section */}
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Progress</p>
              <div className="flex items-center gap-2">
                <Progress
                  value={(project.completedWorkItem / project.totalWorkItem) * 100}
                  className="w-[200px]"
                />
                <span className="text-sm font-medium">
                  {project.completedWorkItem}/{project.totalWorkItem}
                </span>
              </div>
            </div>
            {/* Hours Worked Section */}
            <div className="text-right">
              <p className="text-sm text-muted-foreground flex items-center justify-end gap-1">
                <Clock className="w-4 h-4" /> Hours Worked
              </p>
              <p className="text-sm font-medium">
                {project.hours?.toFixed(1) || 0} hrs
              </p>
            </div>
          </div>
          {/* Last Updated Section */}
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Last Updated</p>
            <p className="text-sm font-medium">
              {new Date(project.lastUpdateTime).toLocaleDateString()}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// Employee Dashboard Component
function EmployeeDashboard({ user }: { user: any }) {
  return (
    <div className="space-y-6">
      {/* Employee Info Card */}
      <Card className="p-6">
        <div className="flex items-center gap-4">
          <div
            style={{
              background: stringToColor(user.mailAddress)
            }}
            className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold"
          >
            {user.displayName.charAt(0).toUpperCase()}
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{user.displayName}</h2>
            <p className="text-gray-500 dark:text-gray-400">{user.mailAddress}</p>
          </div>
        </div>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card className="p-4">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-primary/10">
              <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Projects</h3>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {user.projects.filter((p: any) => p.tasks > 0).length}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-primary/10">
              <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Completed Tasks</h3>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {user.completedWorkItem}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-primary/10">
              <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Pending Tasks</h3>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {user.pendingWorkItem}
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

function ProjectsPage({ user }: { user: any }) {
  const { getProjectsandhoursByUserId } = useProjectsHoursAPI();
  const { centralData } = useCentralAPI({ trigger: false });
  const [projects, setProjects] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<any>(null);

  useEffect(() => {
    if (!user?.originId) {
      setProjects([]);
      return;
    }

    const fetchProjects = async () => {
      setIsLoading(true);
      try {
        const data = await getProjectsandhoursByUserId({ userOriginId: user.originId });
        setProjects(data);
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch projects');
        setProjects([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const organization = centralData?.Organizations || [];

  if (isLoading) return <div>Loading projects...</div>;
  if (error) return <div>Error: {error}</div>;
  const nestedWorkItems = nestWorkItems(
    selectedProject?.workItems
      ?.filter((item: WorkItem) => item.fields['System.WorkItemType'] === 'Issue' || item.fields['System.WorkItemType'] === 'Task')
      .map((item: WorkItem) => {

        return {
          id: item.id,
          rev: item.rev,
          fields: item.fields,
          url: '',
          organization: item.organizationId || '',
          parentId: item.fields["System.Parent"] || null,
        };
      }) || []);

  return (
    <div className="p-6 space-y-6">
      {projects.map((project, index) => (
        <ProjectCard
          key={index}
          project={project}
          onClick={setSelectedProject}
        />
      ))}

      <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
        <DialogContent className="max-w-4xl overflow-y-auto px-0">
          <DialogHeader className="px-5">
            <DialogTitle>{selectedProject?.name} - Work Items</DialogTitle>
            <DialogDescription>
              View and manage work items for this project
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto px-5">
            <div className="grid gap-4">
              <WorkItemList
                workItems={
                  nestedWorkItems
                }
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface IconTabProps {
  value: string;
  icon: React.ReactNode;
  label: string;
}

function IconTab({ value, icon, label }: IconTabProps) {
  return (
    <TabsTrigger value={value} className="relative group">
      {icon}
      <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-vstargray-800 text-white px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
        {label}
      </span>
    </TabsTrigger>
  );
}

function TaskReport({ user }: { user: EmployeeDataType }) {
  const { centralData } = useCentralAPI({ trigger: false });
  const {
    filterWorkItemsByDateRange
  } = useWorkItemsAPIAll()

  const [startDate, setStartDate] = useState<Date>(() => {
    const date = new Date();
    date.setDate(1); // First day of current month
    return date;
  });
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [filteredWorkItems, setFilteredWorkItems] = useState<WorkItemTypeCentral[]>([]);

  useEffect(() => {
    if (centralData?.workItemsWithOrganizationId) {

      const filtered = filterWorkItemsByDateRange({
        workItems: centralData.workItemsWithOrganizationId,
        fromDate: startDate,
        toDate: endDate,
        userId: user?.ids[0]
      });

      setFilteredWorkItems(filtered);
    }
  }, [centralData, startDate, endDate]);

  const nestedWorkItems = nestWorkItems(
    filteredWorkItems?.filter((item) => item.revisions?.[item.revisions.length - 1]?.fields['System.WorkItemType'] === 'Issue' || item.revisions?.[item.revisions.length - 1]?.fields['System.WorkItemType'] === 'Task')
      .map((item) => {

        return {
          id: item.id,
          rev: item.revisions?.[item.revisions.length - 1]?.rev,
          fields: item.revisions?.[item.revisions.length - 1]?.fields,
          url: '',
          organization: item.organizationId || '',
          parentId: item.revisions?.[item.revisions.length - 1]?.fields["System.Parent"] || null,
        };
      }) || []);

  return (
    <div className="space-y-6 p-6">
      <div className="flex gap-4 items-center">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">From:</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-[200px] justify-start text-left font-normal">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {format(startDate, "PPP")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={(date) => date && setStartDate(date)}
              // initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">To:</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-[200px] justify-start text-left font-normal">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {format(endDate, "PPP")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={(date) => date && setEndDate(date)}
              // initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="w-full">
        <WorkItemList
          workItems={
            nestedWorkItems
          }
        />
      </div>
    </div>
  );
}

function TaskDayReport({ user }: { user: EmployeeDataType }) {
  const { centralData } = useCentralAPI({ trigger: false });
  const { filterWorkItemsDayWiseByDateRange } = useWorkItemsAPIAll()

  const [startDate, setStartDate] = useState<Date>(() => {
    const date = new Date();
    date.setDate(1); // First day of current month
    return date;
  });
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [filteredWorkItemsDaywise, setFilteredWorkItemsDaywise] = useState<DayWiseEntry[]>([]);

  useEffect(() => {
    if (centralData?.workItemsWithOrganizationId) {
      const filtered = filterWorkItemsDayWiseByDateRange({
        workItems: centralData.workItemsWithOrganizationId,
        fromDate: startDate,
        toDate: endDate,
        userId: user?.ids[0]
      });
      console.log(filtered)
      setFilteredWorkItemsDaywise(filtered);
    }
  }, [centralData, startDate, endDate]);

  return (
    <div className="space-y-6 p-6">
      <div className="flex gap-4 items-center">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">From:</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-[200px] justify-start text-left font-normal">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {format(startDate, "PPP")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={(date) => date && setStartDate(date)}
              />
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">To:</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-[200px] justify-start text-left font-normal">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {format(endDate, "PPP")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={(date) => date && setEndDate(date)}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="space-y-4">
        {filteredWorkItemsDaywise
          .map((dayEntry) => {
            const startDate = new Date(dayEntry.date);
            const endDate = new Date(dayEntry.date);
            endDate.setHours(23, 59, 59, 999);
            const hours = dayEntry.workItems.filter((item) => item.revisions?.[item.revisions.length - 1]?.fields['System.WorkItemType'] === 'Issue').reduce((total, item) => total + item.hour, 0);

            const formattedDate = format(new Date(dayEntry.date), "dd-MMMM-yyyy");

            return (
              <Card key={dayEntry.date} className="p-4">
                <CardHeader>
                  <CardTitle>{formattedDate} ({hours < 8 ? hours.toFixed(2) : 8} hours) </CardTitle>
                </CardHeader>
                <CardContent>
                  <WorkItemList
                    workItems={nestWorkItems(
                      dayEntry.workItems
                        .filter(
                          (item) =>
                            item.revisions?.[item.revisions.length - 1]?.fields['System.WorkItemType'] === 'Issue' ||
                            item.revisions?.[item.revisions.length - 1]?.fields['System.WorkItemType'] === 'Task'
                        )
                        .map((item) => ({
                          id: item.id,
                          rev: item.revisions?.[item.revisions.length - 1]?.rev,
                          fields: item.revisions?.[item.revisions.length - 1]?.fields,
                          url: '',
                          organization: item.organizationId || '',
                          parentId: item.revisions?.[item.revisions.length - 1]?.fields["System.Parent"] || null,
                          endDate: endDate,
                          startDate: startDate
                        }))
                    )}
                  />
                </CardContent>
              </Card>
            )
          })}
      </div>
    </div>
  );
}


export type EmployeeDataType = {
  displayName: string
  mailAddress: string
  completedWorkItem: number
  pendingWorkItem: number
  originId: string,
  ids: string[],
  projects: {
    id: number
    name: string
    organizationId: string
    totalWorkItem: number
    completedWorkItem: number
    tasks: number
  }[]
} | null

export function EmployeePage() {
  const { id } = useParams()
  const [user, setUser] = React.useState<EmployeeDataType>(null)
  const [isLoading, setIsLoading] = React.useState(true)

  const {
    getUserById
  } = useUsersAPI()

  const {
    user: userTasks,
    isLoading: isLoadingUser,
    error
  } = useUserTaskAPI({
    id: id || null
  })

  // console.log(userTasks)

  React.useEffect(() => {
    // Here you would typically fetch the user data using the id
    // For now, we'll simulate it with a timeout
    if (id) {
      const user = getUserById({ id })
      if (user) {
        setUser({
          displayName: user.user.displayName,
          mailAddress: user.user.mailAddress,
          completedWorkItem: user.completedWorkItem,
          pendingWorkItem: user.totalWorkItem - user.completedWorkItem,
          originId: user.user.originId,
          ids: user.id,
          projects: user.projects.map((project) => ({
            id: 1,
            name: project.name,
            organizationId: project.organizationId,
            totalWorkItem: project.totalWorkItem,
            completedWorkItem: project.completedWorkItem,
            tasks: project.totalWorkItem
          }))
        })
        setIsLoading(false)
      }
    }
  }, [id])

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 animate-pulse"></div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Tabs defaultValue="dashboard" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <IconTab
            value="dashboard"
            icon={<IconDashboard className="w-5 h-5" />}
            label="Dashboard"
          />
          <IconTab
            value="Current tasks"
            icon={<IconListCheck className="w-5 h-5" />}
            label="Current tasks"
          />
          <IconTab
            value="projects"
            icon={<IconFolder className="w-5 h-5" />}
            label="Projects"
          />
          <IconTab
            value="task-report"
            icon={<IconReport className="w-5 h-5" />}
            label="Task Report"
          />
          <IconTab
            value="task-daywise"
            icon={<IconCalendar className="w-5 h-5" />}
            label="Task Daywise"
          />
        </TabsList>
        <TabsContent value="dashboard">
          <EmployeeDashboard user={user} />
        </TabsContent>
        <TabsContent value="Current tasks">
          <div className=' flex flex-col gap-1 '>
            {userTasks?.projects?.filter(project => project.tasks.length > 0).map(
              project => {
                return (
                  <ProjectComponentTaskVarient
                    key={project.id}
                    organization={project.organisationName}
                    projectName={project.name}
                    tasks={project.tasks}
                  />
                )
              }
            )}
          </div>
        </TabsContent>
        <TabsContent value="projects">
          <ProjectsPage user={user} />
        </TabsContent>
        <TabsContent value="task-report">
          <TaskReport user={user} />
        </TabsContent>
        <TabsContent value="task-daywise">
          <TaskDayReport user={user} />
        </TabsContent>
      </Tabs>
    </div>
  )
}